import { Injectable, signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class WakeLockService {
  readonly active = signal(false);

  private readonly supported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;
  private sentinel: WakeLockSentinel | null = null;
  private wanted = false;

  constructor() {
    if (!this.supported || typeof document === 'undefined') {
      return;
    }

    // The platform releases the sentinel every time the page is hidden. Without
    // re-acquiring, the first time an operator checks a message the screen
    // starts sleeping again for the rest of the service.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.wanted) {
        void this.acquire();
      }
    });
  }

  async enable(): Promise<void> {
    this.wanted = true;
    await this.acquire();
  }

  async disable(): Promise<void> {
    this.wanted = false;
    await this.release();
  }

  private async acquire(): Promise<void> {
    if (!this.supported || this.sentinel) {
      return;
    }
    try {
      this.sentinel = await navigator.wakeLock.request('screen');
      this.active.set(true);
      this.sentinel.addEventListener('release', () => {
        this.sentinel = null;
        this.active.set(false);
      });
    } catch {
      // NotAllowedError (hidden document, battery saver) and NotSupportedError
      // are both non-events: the screen just behaves as it did before.
    }
  }

  private async release(): Promise<void> {
    if (!this.sentinel) {
      return;
    }
    try {
      await this.sentinel.release();
    } catch {
      // Already released by the platform.
    }
    this.sentinel = null;
    this.active.set(false);
  }
}
